import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useVerifyOTPMutation, useResendRegistrationOtpMutation, useCompleteRegistrationMutation } from "../../../../../service/customer-service-v2";
import { logIn } from "../../../../../stores/slices/current-user-slice";
import { getErrorMessage } from "../../../../../utils/error-handler";
import {
	trackEmailOtpResent,
	trackEmailVerified,
	trackPhoneOtpResent,
	trackPhoneOtpSent,
	trackPhoneVerified,
} from "../../../../../services/business-analytics/onboardingEvents";
import styles from "./multi-step-form.module.scss";
import PasscodeOTPInput from "../passcode-otp-input/PasscodeOTPInput";
import Button from "../../../../../common/ekedc-components/button/Button";

interface Props {
	signupId: string;
	email: string;
	phone: string;
	onComplete: () => void;
}

export const Step4OTPVerification: React.FC<Props> = ({ signupId, email, phone, onComplete }) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [emailOtp, setEmailOtp] = useState("");
	const [phoneOtp, setPhoneOtp] = useState("");
	const [otpPhase, setOtpPhase] = useState<"email" | "phone">("email");
	const emailAttempts = useRef(0);
	const phoneAttempts = useRef(0);
	const emailResendCount = useRef(0);
	const phoneResendCount = useRef(0);

	const [verifyOTP, { isLoading: isVerifyingOTP }] = useVerifyOTPMutation();
	const [resendOTP, { isLoading: isOTPResend }] = useResendRegistrationOtpMutation();
	const [completeRegistration, { isLoading: isCompletingRegistration }] = useCompleteRegistrationMutation();

	const handleEmailOtpSubmit = async () => {
		const attempts = ++emailAttempts.current;
		try {
			await verifyOTP({ signupId, otp: emailOtp, type: "email" }).unwrap();
			trackEmailVerified("success", attempts);
			// Verifying the email is what sends the phone OTP; there is no separate send call.
			trackPhoneOtpSent(phone);
			setOtpPhase("phone");
			setEmailOtp("");
		} catch (err: any) {
			trackEmailVerified("failed", attempts);
			toast.error(err?.data?.title || t("auth.web.invalidCodeTryAgain"));
		}
	};

	const handlePhoneOtpSubmit = async () => {
		const attempts = ++phoneAttempts.current;
		// Completing the registration can still fail after the code is accepted; that is not a failed verification.
		let verified = false;
		try {
			await verifyOTP({ signupId, otp: phoneOtp, type: "phone" }).unwrap();
			verified = true;
			trackPhoneVerified("success", attempts);
			const loginResponse = await completeRegistration({ signupId }).unwrap();
			dispatch(logIn({ token: loginResponse.token, refreshToken: loginResponse.refreshToken, userType: loginResponse.userType }));
			onComplete();
		} catch (err: any) {
			if (!verified) trackPhoneVerified("failed", attempts);
			toast.error(err?.data?.title || t("common.labels.resetFlowError"));
		}
	};

	const handleResendOTP = async () => {
		if (otpPhase === "email") trackEmailOtpResent(email, ++emailResendCount.current);
		else trackPhoneOtpResent(phone, ++phoneResendCount.current);
		try {
			await resendOTP({ signupId, type: otpPhase }).unwrap();
			toast.success(t("auth.web.otpResentSuccessfully"));
		} catch (err: any) {
			toast.error(getErrorMessage(err?.data) || t("common.labels.somethingWrong"));
		}
	};

	return (
		<div className={styles.stepContent}>
			{otpPhase === "email" ? (
				<>
				<div className={styles.card}>
					<div className={styles.cardHeader}>
						<span className={styles.cardLabel}>{t("auth.letsStartWithEmailVerification")}</span>
					</div>
					<PasscodeOTPInput
						value={emailOtp}
						onChange={setEmailOtp}
						onResend={handleResendOTP}
						otpLength={6}
						resendTimerSeconds={180}
						disabled={isVerifyingOTP || isOTPResend}
						sendToEmail={true}
						renderSeparator={true}
						contact={email}
						dialCode=""
					/>
				</div>
				<div className={styles.buttonSection}>

					<Button
						text={isVerifyingOTP ? t("auth.web.verifying") : t("auth.web.verifyEmail")}
						onClick={handleEmailOtpSubmit}
						disabled={emailOtp.length < 6 || isVerifyingOTP}
					/>
				</div>
				</>
			) : (
				<>
				<div className={styles.felxGapSection}>

					<div className={styles.card}>
						<div className={styles.cardHeader}></div>
						<div className={styles.verifiedBadge}>
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none">
								<circle cx="12" cy="12" r="10" fill="var(--app-status-success)" />
								<path d="M7 12l4 4 6-7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
							</svg>
							<span>{t("auth.emailAddressVerified")}</span>
						</div>
					</div>
					<div className={styles.card}>
						<div className={styles.cardHeader}>
							<span className={styles.cardLabel}>{t("auth.yayNowVerifyPhone")}</span>
						</div>
						<PasscodeOTPInput
							value={phoneOtp}
							onChange={setPhoneOtp}
							onResend={handleResendOTP}
							otpLength={4}
							resendTimerSeconds={180}
							renderSeparator={false}
							disabled={isVerifyingOTP || isOTPResend || isCompletingRegistration}
							sendToEmail={false}
							contact={phone}
							dialCode=""
						/>
					</div>
				</div>
				<div className={styles.buttonSection}>
						<Button
							text={isVerifyingOTP || isCompletingRegistration ? t("auth.web.verifying") : t("auth.web.verifyPhone")}
							onClick={handlePhoneOtpSubmit}
							disabled={phoneOtp.length < 4 || isVerifyingOTP || isCompletingRegistration}
						/>
				</div>
				</>
			)}
		</div>
	);
};
